import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import {
  completeAccountingDocumentUpload,
  createAccountingDocumentUploadUrl,
  deleteAccountingDocument,
  getAccountingDocuments,
} from "../../api/accounting";
import { useAuth } from "../../context/AuthContext";
import SalonBusinessCpaShell from "./SalonBusinessCpaShell";

const statusClasses = {
  upload_requested: "bg-amber-50 text-amber-700 border-amber-200",
  received: "bg-emerald-50 text-emerald-700 border-emerald-200",
  being_reviewed: "bg-sky-50 text-sky-700 border-sky-200",
  needs_new_copy: "bg-rose-50 text-rose-700 border-rose-200",
  done: "bg-slate-100 text-slate-700 border-slate-200",
};

const documentStatusOptions = [
  { code: "upload_requested", label: "Upload started" },
  { code: "received", label: "Received" },
  { code: "being_reviewed", label: "Being reviewed" },
  { code: "needs_new_copy", label: "Need a new copy" },
  { code: "done", label: "Done" },
];

function formatFileSize(value) {
  const size = Number(value || 0);
  if (!Number.isFinite(size) || size <= 0) return "Size pending";
  if (size < 1024 * 1024) return `${Math.max(1, Math.round(size / 1024))} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(date);
}

function monthValue(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 7);
}

export default function SalonBusinessCpaDocuments() {
  const { token } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [typeOptions, setTypeOptions] = useState([]);
  const [suggestedCodes, setSuggestedCodes] = useState([]);
  const [documentType, setDocumentType] = useState("");
  const [monthFilter, setMonthFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [file, setFile] = useState(null);
  const [note, setNote] = useState("");
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [deleteId, setDeleteId] = useState(null);
  const requestId = searchParams.get("request_id") || "";

  const suggestedOptions = useMemo(() => {
    const suggested = new Set(suggestedCodes || []);
    return typeOptions.filter((option) => suggested.has(option.code));
  }, [suggestedCodes, typeOptions]);

  const otherOptions = useMemo(() => {
    const suggested = new Set(suggestedCodes || []);
    return typeOptions.filter((option) => !suggested.has(option.code));
  }, [suggestedCodes, typeOptions]);

  const filteredDocuments = useMemo(() => {
    return documents.filter((document) => {
      if (monthFilter && monthValue(document.created_at_utc) !== monthFilter) return false;
      if (statusFilter && document.status !== statusFilter) return false;
      if (typeFilter && document.document_type !== typeFilter) return false;
      return true;
    });
  }, [documents, monthFilter, statusFilter, typeFilter]);

  const loadDocuments = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await getAccountingDocuments(token);
      const options = Array.isArray(data?.document_type_options)
        ? data.document_type_options
        : [];
      const suggestions = Array.isArray(data?.suggested_document_type_codes)
        ? data.suggested_document_type_codes
        : [];
      const requestedType = searchParams.get("document_type") || "";
      const requestedTypeIsValid = options.some((option) => option.code === requestedType);
      setDocuments(Array.isArray(data?.documents) ? data.documents : []);
      setTypeOptions(options);
      setSuggestedCodes(suggestions);
      setDocumentType((current) =>
        requestedTypeIsValid
          ? requestedType
          : current || suggestions[0] || options[0]?.code || "other"
      );
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to load documents.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      loadDocuments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, searchParams]);

  const handleUpload = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess("");
    if (!file) {
      setError("Choose a document to upload.");
      return;
    }
    if (!documentType) {
      setError("Choose what kind of document this is.");
      return;
    }
    setUploading(true);
    try {
      const upload = await createAccountingDocumentUploadUrl(token, {
        document_type: documentType,
        file_name: file.name,
        content_type: file.type || "application/octet-stream",
        file_size: file.size,
        note,
        request_id: requestId ? Number(requestId) : undefined,
      });
      const putResponse = await fetch(upload.upload_url, {
        method: upload.method || "PUT",
        headers: upload.required_headers || {
          "Content-Type": file.type || "application/octet-stream",
        },
        body: file,
      });
      if (!putResponse.ok) {
        throw new Error("Upload failed. Please try again.");
      }
      await completeAccountingDocumentUpload(token, upload.document.id);
      setFile(null);
      setNote("");
      setSuccess("We received your document.");
      await loadDocuments();
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err?.message ||
          "Unable to upload this document."
      );
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    setError("");
    setSuccess("");
    try {
      await deleteAccountingDocument(token, id);
      setDeleteId(null);
      setSuccess("Document removed.");
      await loadDocuments();
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to remove this document.");
    }
  };

  return (
    <SalonBusinessCpaShell
      title="Documents"
      description="Upload the records your accountant needs. Keep it simple: choose the type, pick the file, and upload."
    >
      <section className="grid gap-6 lg:grid-cols-[minmax(0,420px)_1fr]">
        <form
          onSubmit={handleUpload}
          className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm"
        >
          <h2 className="text-lg font-semibold text-slate-950">Upload a document</h2>
          <p className="mt-2 text-sm leading-6 text-slate-600">
            Your upload is stored privately and connected to this salon.
          </p>
          {searchParams.get("document_type") ? (
            <p className="mt-3 rounded-2xl border border-rose-100 bg-rose-50 px-3 py-2 text-sm text-rose-700">
              We selected the upload type from your {requestId ? "document request" : "checklist task"}.
              {requestId ? " This upload will be connected to that request." : " You can change it if needed."}
            </p>
          ) : null}

          {error ? (
            <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
              {error}
            </div>
          ) : null}
          {success ? (
            <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700">
              {success}
            </div>
          ) : null}

          <label className="mt-5 block text-sm font-semibold text-slate-700">
            What are you uploading?
            <select
              value={documentType}
              onChange={(event) => setDocumentType(event.target.value)}
              disabled={Boolean(requestId)}
              className="mt-2 h-11 w-full rounded-2xl border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none focus:border-rose-300 focus:ring-2 focus:ring-rose-100"
            >
              {suggestedOptions.length ? (
                <optgroup label="Suggested for you">
                  {suggestedOptions.map((option) => (
                    <option key={option.code} value={option.code}>
                      {option.label}
                    </option>
                  ))}
                </optgroup>
              ) : null}
              {otherOptions.length ? (
                <optgroup label="More choices">
                  {otherOptions.map((option) => (
                    <option key={option.code} value={option.code}>
                      {option.label}
                    </option>
                  ))}
                </optgroup>
              ) : null}
            </select>
          </label>

          <label className="mt-5 block text-sm font-semibold text-slate-700">
            File
            <input
              type="file"
              onChange={(event) => setFile(event.target.files?.[0] || null)}
              className="mt-2 block w-full rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-600 file:mr-4 file:rounded-full file:border-0 file:bg-slate-950 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-white"
            />
          </label>
          {file ? (
            <p className="mt-2 text-xs text-slate-500">
              Selected: {file.name} ({formatFileSize(file.size)})
            </p>
          ) : null}

          <label className="mt-5 block text-sm font-semibold text-slate-700">
            Note, optional
            <textarea
              value={note}
              onChange={(event) => setNote(event.target.value)}
              rows={3}
              className="mt-2 w-full rounded-2xl border border-slate-200 px-3 py-3 text-sm text-slate-700 outline-none focus:border-rose-300 focus:ring-2 focus:ring-rose-100"
              placeholder="Example: April bank statement or T4 from my salon."
            />
          </label>

          <button
            type="submit"
            disabled={uploading}
            className="mt-5 rounded-full bg-rose-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-rose-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {uploading ? "Uploading..." : "Upload document"}
          </button>
        </form>

        <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-950">
                Uploaded documents
              </h2>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                Use the status to see what happened next.
              </p>
            </div>
            <button
              type="button"
              onClick={loadDocuments}
              className="rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              Refresh
            </button>
          </div>

          <div className="mt-5 space-y-3">
            <div className="grid gap-3 md:grid-cols-3">
              <label className="text-sm font-semibold text-slate-700">
                Upload month
                <input
                  type="month"
                  value={monthFilter}
                  onChange={(event) => setMonthFilter(event.target.value)}
                  className="mt-2 h-11 w-full rounded-2xl border border-slate-200 px-3 text-sm text-slate-700"
                />
              </label>
              <label className="text-sm font-semibold text-slate-700">
                Status
                <select
                  value={statusFilter}
                  onChange={(event) => setStatusFilter(event.target.value)}
                  className="mt-2 h-11 w-full rounded-2xl border border-slate-200 bg-white px-3 text-sm text-slate-700"
                >
                  <option value="">All statuses</option>
                  {documentStatusOptions.map((option) => (
                    <option key={option.code} value={option.code}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-sm font-semibold text-slate-700">
                Record type
                <select
                  value={typeFilter}
                  onChange={(event) => setTypeFilter(event.target.value)}
                  className="mt-2 h-11 w-full rounded-2xl border border-slate-200 bg-white px-3 text-sm text-slate-700"
                >
                  <option value="">All types</option>
                  {typeOptions.map((option) => (
                    <option key={option.code} value={option.code}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            {loading ? (
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">
                Loading documents...
              </div>
            ) : filteredDocuments.length ? (
              filteredDocuments.map((document) => (
                <article
                  key={document.id}
                  className="rounded-2xl border border-slate-200 bg-slate-50 p-4"
                >
                  <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                    <div>
                      <h3 className="text-sm font-semibold text-slate-950">
                        {document.file_name}
                      </h3>
                      <p className="mt-1 text-sm text-slate-600">
                        {document.document_type_label} - {formatFileSize(document.file_size)}
                      </p>
                      <p className="mt-1 text-xs text-slate-500">
                        Uploaded {formatDate(document.created_at_utc)}
                      </p>
                      {document.note ? (
                        <p className="mt-2 text-sm text-slate-600">{document.note}</p>
                      ) : null}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <span
                        className={`rounded-full border px-3 py-1 text-xs font-semibold ${
                          statusClasses[document.status] ||
                          "border-slate-200 bg-slate-100 text-slate-700"
                        }`}
                      >
                        {document.status_label}
                      </span>
                      {deleteId === document.id ? (
                        <>
                          <button
                            type="button"
                            onClick={() => handleDelete(document.id)}
                            className="rounded-full bg-rose-500 px-3 py-1 text-xs font-semibold text-white hover:bg-rose-600"
                          >
                            Remove
                          </button>
                          <button
                            type="button"
                            onClick={() => setDeleteId(null)}
                            className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-white"
                          >
                            Keep
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setDeleteId(document.id)}
                          className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-white"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                </article>
              ))
            ) : (
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 text-sm text-slate-600">
                No documents uploaded yet.
              </div>
            )}
          </div>
        </section>
      </section>
    </SalonBusinessCpaShell>
  );
}
